<template>
  <div>
    <div class="card">
      <div class="card-header">
        <div class="row">
          <div class="col">
            <h5 class="card-title">Aufgaben</h5>
          </div>
          <div class="col-md-auto text-right">
            <p-button type="success" @click="newTask" class="mt-0"><i class="bi bi-file-earmark-plus"></i> Neue Aufgabe</p-button>
          </div>
        </div>
      </div>
      <div class="card-body row">
        <div class="col-sm-12 mb-3">
          <div class="form-row">
            <div class="col-md-4">
              <label class="mr-2"><b>Status</b></label>
              <el-select
                class="select mb-0 w-100"
                placeholder="Status wählen"
                v-model="filterStatus"
              >
                <el-option label="Alle" value="alle"></el-option>
                <el-option label="Offen" value="offen"></el-option>
                <el-option label="In Bearbeitung" value="In Bearbeitung"></el-option>
                <el-option label="Erledigt" value="Erledigt"></el-option>
              </el-select>
            </div>
            <div class="col-md-4">
              <label class="mr-2"><b>Zugewiesen an</b></label>
              <el-select
                class="select mb-0 w-100"
                placeholder="Mitarbeiter filtern"
                filterable
                v-model="filterAssignee"
              >
                <el-option label="Alle" value="__all__"></el-option>
                <el-option label="Mir zugewiesen" value="__me__"></el-option>
                <el-option
                  v-for="option in staff"
                  :key="option.staffName"
                  :value="option.staffName"
                  :label="option.staffName"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-4 d-flex align-items-end">
              <el-checkbox v-model="filterHideDone">Erledigte ausblenden</el-checkbox>
            </div>
          </div>
        </div>
        <div class="col-sm-12">
          <el-collapse accordion>
            <el-collapse-item :name="task.key" v-for="task in filteredTasks" :key="task.key">
              <template slot="title">
                <div class="task-title pl-2">
                  <div class="task-title-main">
                    <i class="bi bi-circle text-muted" v-if="task.taskStatus === 'offen'"></i>
                    <i class="bi bi-arrow-clockwise text-warning" v-if="task.taskStatus === 'In Bearbeitung'"></i>
                    <i class="bi bi-check-circle text-success" v-if="task.taskStatus === 'Erledigt'"></i>
                    <b class="task-title-name"> {{task.taskName}}</b>
                  </div>
                  <div class="task-title-meta">
                    <span class="badge badge-pill" :class="{'badge-secondary': task.taskStatus === 'offen', 'badge-warning': task.taskStatus === 'In Bearbeitung', 'badge-success': task.taskStatus === 'Erledigt'}">{{ task.taskStatus }}</span>
                    <small class="text-muted" v-if="task.taskAssignee && task.taskAssignee !== '-'"><i class="bi bi-person-fill"></i> {{ task.taskAssignee }}</small>
                    <small class="text-muted">({{ task.taskCreatedAtFormatted }})</small>
                  </div>
                </div>
              </template>
              <div class="row pl-5">
                <div class="col-md-12">
                  <p class="mb-1"><b>Beschreibung:</b></p>
                  <p style="white-space: pre-line;">{{ task.taskDescription }}</p>
                </div>
              </div>
              <div class="row pl-5">
                <div class="col-md-6">
                  <p><b>Zu erledigen bis:</b> {{ task.taskDueDateFormatted }}</p>
                </div>
                <div class="col-md-6">
                  <p><b>Bearbeiter:</b> {{ task.taskAssignee }}</p>
                </div>
              </div>
              <div class="row pl-5">
                <div class="col-md-12">
                  <p><b>Erstellt von:</b> {{ task.taskCreator }}</p>
                </div>
              </div>
              <div class="row pl-5">
                <div class="col-md-12">
                  <p><b>Status: </b>
                    <span class="badge" :class="{'badge-secondary': task.taskStatus === 'offen', 'badge-warning': task.taskStatus === 'In Bearbeitung', 'badge-success': task.taskStatus === 'Erledigt'}">
                      {{ task.taskStatus }}
                    </span>
                    <small v-if="task.taskStatus === 'Erledigt'" class="ml-2">({{ task.taskCompletedAt }})</small>
                  </p>
                </div>
              </div>
              <div class="row pl-5" v-if="task.taskComment && task.taskComment.length > 0 && task.taskStatus == 'Erledigt'">
                <div class="col-md-12">
                  <p class="mb-1"><b>Anmerkung: </b></p>
                  <p style="white-space: pre-line;">{{ task.taskComment }}</p>
                </div>
              </div>

              <div class="row pl-3 pb-3">
                <div class="col-md-12 text-right task-actions">
                  <p-button
                    type="warning"
                    size="md"
                    @click="setTaskInProgress(task)"
                    :disabled="task.taskStatus !== 'offen'"
                  >
                    <i class="bi bi-arrow-clockwise"></i> In Bearbeitung
                  </p-button>
                  <p-button
                    type="primary"
                    size="md"
                    @click="setTaskDone(task)"
                    :disabled="task.taskStatus === 'Erledigt'"
                  >
                    <i class="bi bi-check2-square"></i> Erledigt
                  </p-button>
                  <p-button
                    type="default"
                    size="md"
                    title="Aufgabe bearbeiten"
                    @click="editTask(task)"
                    :disabled="task.taskStatus === 'Erledigt'"
                  >
                    <i class="bi bi-pencil"></i>
                  </p-button>
                  <p-button
                    type="danger"
                    size="md"
                    title="Aufgabe löschen"
                    @click="removeTask(task.key, task.taskName)"
                  >
                    <i class="bi bi-trash"></i>
                  </p-button>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
    </div>
    <small class="text-muted"><i><b>Hinweis:</b> Erledigte Aufgaben werden nach 7 Tagen automatisch ausgeblendet.</i></small>

    <!-- Neue Aufgabe Modal -->
    <el-dialog :title=" dialogMode === 'create' ? 'Neue Aufgabe erstellen' : 'Aufgabe bearbeiten'" :visible.sync="newTaskDialog" :close-on-click-modal="false">
      <el-form :model="newTaskForm">
        <div class="row">
          <div class="col-md-12">
              <el-form-item label="Titel">
                <el-input v-model="newTaskTitle" placeholder="Titel der Aufgabe" required></el-input>
              </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
              <el-form-item label="Beschreibung">
                <el-input
                  type="textarea"
                  v-model="newTaskDescription"
                  placeholder="Beschreibung der Aufgabe"
                  rows="4"
                ></el-input>
              </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <el-form-item label="Zu erledigen bis">
              <el-date-picker
                    v-model="newTaskDueDate"
                    type="date"
                    placeholder="Fälligkeitsdatum"
                    :picker-options="pickerOptions1"
                    format="dd.MM.yyyy"
                  >
              </el-date-picker>
            </el-form-item>
          </div>
          <div class="col-md-6">
            <el-form-item label="Erstellt von">
              <el-select
                    class="select mb-0 w-100"
                    placeholder="Mitarbeiter wählen"
                    filterable
                    v-model="newTaskCreator"
                  >
                    <el-option
                      v-for="option in staff"
                      class="select"
                      :value="option.staffName"
                      :label="option.staffName"
                      :key="`creator-${option.staffName}`"
                    >
                    </el-option>
              </el-select>
            </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <el-form-item label="Bearbeiter">
              <el-select
                    class="select mb-0 w-100"
                    placeholder="Mitarbeiter wählen"
                    filterable
                    v-model="newTaskAssignee"
                  >
                    <el-option
                      v-for="option in staff"
                      class="select"
                      :value="option.staffName"
                      :label="option.staffName"
                      :key="`assignee-${option.staffName}`"
                    >
                    </el-option>
              </el-select>
              <small class="text-muted pt-0"><i>Der gewählte Bearbeiter wird per E-Mail benachrichtigt.</i></small>
            </el-form-item>
          </div>
          <div class="col-md-6"></div>
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <p-button @click="newTaskDialog = false">Abbrechen</p-button>
        <p-button type="primary" @click="addTask" :disabled="!newTaskTitle" v-if="dialogMode === 'create'">Speichern</p-button>
        <p-button type="primary" @click="updateTask" :disabled="!newTaskTitle" v-if="dialogMode === 'edit'">Speichern</p-button>
      </span>
    </el-dialog>

    <!-- Add comment dialog -->
    <el-dialog title="Aufgabe erledigen" :visible.sync="commentDialog" :close-on-click-modal="false">
      <el-form :model="commentForm">
        <div class="row">
          <div class="col-md-12">
              <el-form-item label="Anmerkung">
                <el-input
                  type="textarea"
                  v-model="commentForm.comment"
                  placeholder="Füge eine Anmerkung hinzu..."
                  rows="3"
                ></el-input>
              </el-form-item>
          </div>
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <p-button @click="commentDialog = false">Abbrechen</p-button>
        <p-button type="primary" @click="markTaskDone">Erledigt</p-button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import Vue from "vue";
import { db } from "../../../../firebaseDb";
import { Table, TableColumn, Tooltip, Select, Option, Input, InputNumber, Collapse, CollapseItem, MessageBox, Dialog, Form, FormItem, Checkbox } from "element-ui";
import router from "../../../../main";
import moment from "moment-timezone";
import store from "../../../../store";
Vue.use(Table);
Vue.use(TableColumn);
Vue.use(Tooltip);


export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber,
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    MessageBox,
    [Dialog.name]: Dialog,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Checkbox.name]: Checkbox,
  },

  data() {
    return {
      tasks: [],
      staff: [],
      newTaskTitle: null,
      newTaskDescription: null,
      newTaskDueDate: null,
      newTaskCreator: null,
      newTaskAssignee: null,
      newTaskForm: {},
      editTaskKey: null,
      newTaskDialog: false,
      commentDialog: false,
      commentForm: { comment: "" },
      activeTaskKey: null,
      dialogMode: "create",
      location: store.getters.userlocation,
      role: store.getters.userrole,
      filterStatus: "alle",
      filterHideDone: false,
      filterAssignee: "__all__",
      pickerOptions1: {
        firstDayOfWeek: 1,
        shortcuts: [
          {
            text: "Morgen",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + 3600 * 1000 * 24);
              picker.$emit("pick", date);
            },
          },
          {
            text: "In 1 Woche",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + 3600 * 1000 * 24 * 7);
              picker.$emit("pick", date);
            },
          },
          {
            text: "In 2 Wochen",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + 3600 * 1000 * 24 * 14);
              picker.$emit("pick", date);
            },
          },
          {
            text: "In 1 Monat",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + 3600 * 1000 * 24 * 30);
              picker.$emit("pick", date);
            },
          },
        ],
      },
    };
  },
  created() {
    this.loadTasks();
    this.getStaff();
    store.watch(
      (state) => state.userlocation,
      () => {
        this.location = store.getters.userlocation;
        this.loadTasks();
      }
    );
  },
  computed: {
    currentUserName() {
      const user = store.getters.user;
      return user && user.data && user.data.displayName ? user.data.displayName : null;
    },
    filteredTasks() {
      return this.tasks.filter((task) => {
        if (this.filterHideDone && task.taskStatus === "Erledigt") {
          return false;
        }

        if (this.filterStatus !== "alle" && task.taskStatus !== this.filterStatus) {
          return false;
        }

        if (this.filterAssignee === "__me__") {
          if (!this.currentUserName || task.taskAssignee !== this.currentUserName) {
            return false;
          }
        } else if (this.filterAssignee && this.filterAssignee !== "__all__" && task.taskAssignee !== this.filterAssignee) {
          return false;
        }

        return true;
      });
    },
  },
  methods: {
    loadTasks() {
      const tasksRef = db.collection("tasks");
      tasksRef.where('locationID', '==', this.location).where('Hidden', '==', false).orderBy("CreatedAt", "desc").onSnapshot((snapshotChange) => {
        this.tasks = [];
        snapshotChange.forEach((doc) => {
          if (doc.data().DueDate && doc.data().DueDate.toDate) {
            doc.data().DueDate = doc.data().DueDate.toDate().toLocaleDateString("de-DE");
          }
          this.tasks.push({
            key: doc.id,
            taskName: doc.data().Title,
            taskDescription: doc.data().Description || "-",
            taskDueDate: doc.data().DueDate,
            taskDueDateFormatted: doc.data().DueDate ? moment(new Date(doc.data().DueDate.seconds * 1000)).format("DD. MMM YYYY").toString() : "-",
            taskCreator: doc.data().Creator || "-",
            taskAssignee: doc.data().Assignee || "-",
            taskStatus: doc.data().Status,
            taskComment: doc.data().Comment,
            taskCreatedAt: doc.data().CreatedAt,
            taskCreatedAtFormatted: moment(new Date(doc.data().CreatedAt.seconds * 1000)).format("DD. MMM YYYY").toString(),
            taskCompletedAt: doc.data().CompletedAt ? moment(new Date(doc.data().CompletedAt.seconds * 1000)).format("DD. MMM YYYY").toString() : null,
          });
        });
      });
    },
    getStaff() {
      // get list of staff
      const staffRef = db.collection("trainer");
      staffRef
        .orderBy("Name")
        .onSnapshot((snapshotChange) => {
          this.staff = [];
          snapshotChange.forEach((doc) => {
            this.staff.push({
              staffId: doc.id,
              staffName: doc.data().Name,
            });
          });
        });
    },
    newTask() {
      this.dialogMode = "create";
      this.newTaskDialog = true;
      this.newTaskTitle = null;
      this.newTaskDescription = null;
      this.newTaskDueDate = null;
      this.newTaskCreator = this.currentUserName;
      this.newTaskAssignee = null;
      this.editTaskKey = null;
    },
    editTask(task) {
      this.dialogMode = "edit";
      this.newTaskTitle = task.taskName;
      this.newTaskDescription = task.taskDescription;
      this.newTaskDueDate = task.taskDueDate;
      this.newTaskCreator = task.taskCreator === "-" ? this.currentUserName : task.taskCreator;
      this.newTaskAssignee = task.taskAssignee;
      this.editTaskKey = task.key;
      this.newTaskDialog = true;
    },
    removeTask(taskKey, taskName) {
      MessageBox.confirm(
        "Soll die Aufgabe " + taskName + " wirklich gelöscht werden?",
        "Warnung",
        {
          confirmButtonText: "Löschen",
          cancelButtonText: "Abbrechen",
          type: "warning",
          callback: (action) => {
            if (action == "confirm") {
              db.collection("tasks").doc(taskKey).update({ Hidden: true }).then(() => {
                console.log("Aufgabe " + taskName + " removed.");
                this.loadTasks()
              }).catch(() => {
                console.log("Error removing task.");
              });
            }
          },
        }
      );
    },
    addTask() {
      db.collection("tasks")
        .add({
          Title: this.newTaskTitle,
          Description: this.newTaskDescription,
          DueDate: this.newTaskDueDate,
          Creator: this.newTaskCreator || this.currentUserName || "-",
          Assignee: this.newTaskAssignee,
          CreatedAt: new Date(),
          LastUpdated: new Date(),
          CompletedAt: null,
          Status: "offen",
          Hidden: false,
          locationID: this.location,
        })
        .then(() => {
          this.newTaskTitle = null;
          this.newTaskDescription = null;
          this.newTaskDueDate = null;
          this.newTaskCreator = this.currentUserName;
          this.newTaskAssignee = null;
          this.newTaskDialog = false;
          console.log("Task added.");
        })
        .catch(() => {
          console.log("Adding task failed");
        });
    },
    updateTask() {
      db.collection("tasks")
        .doc(this.editTaskKey)
        .update({
          Title: this.newTaskTitle,
          Description: this.newTaskDescription,
          DueDate: this.newTaskDueDate,
          Creator: this.newTaskCreator || this.currentUserName || "-",
          Assignee: this.newTaskAssignee,
          LastUpdated: new Date(),
        })
        .then(() => {
          this.newTaskTitle = null;
          this.newTaskDescription = null;
          this.newTaskDueDate = null;
          this.newTaskCreator = this.currentUserName;
          this.newTaskAssignee = null;
          this.editTaskKey = null;
          this.newTaskDialog = false;
          console.log("Task updated.");
        })
        .catch(() => {
          console.log("Updating task failed");
        });
    },
    setTaskDone(task) {
      this.activeTaskKey = task.key;
      this.commentForm.comment = "";
      this.commentDialog = true;
    },
    markTaskDone() {
      db.collection("tasks")
        .doc(this.activeTaskKey)
        .update({
          Status: "Erledigt",
          Comment: this.commentForm.comment,
          CompletedAt: new Date(),
          LastUpdated: new Date(),
        })
        .then(() => {
          this.commentForm.comment = "";
          this.activeTaskKey = null;
          this.commentDialog = false;
          console.log("Task marked as done.");
        })
        .catch(() => {
          console.log("Marking task as done failed");
        });
    },
    setTaskInProgress(task) {
      db.collection("tasks")
        .doc(task.key)
        .update({
          Status: "In Bearbeitung",
          LastUpdated: new Date(),
        })
        .then(() => {
          console.log("Task set to in progress.");
        })
        .catch(() => {
          console.log("Setting task to in progress failed");
        });
    },
  },
};
</script>
<style>
  .el-form-item__label {
    font-weight: bold;
    line-height: normal !important;
  }

  /* Task list title styling – wraps cleanly on mobile */
  .task-title {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    line-height: 1.4;
  }
  .task-title-main {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 1.1em;
    min-width: 0;
    flex: 1 1 auto;
  }
  .task-title-name {
    white-space: normal;
    word-break: break-word;
  }
  .task-title-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.6rem;
  }
  /* Allow the collapse header to grow with wrapped content */
  .el-collapse-item__header {
    height: auto !important;
    min-height: 48px;
    line-height: 1.4 !important;
    white-space: normal !important;
    align-items: flex-start;
  }

  /* Action buttons wrap on small screens */
  .task-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    justify-content: flex-end;
  }

  @media (max-width: 576px) {
    .task-title-main {
      font-size: 1em;
      flex-basis: 100%;
    }
    .task-title-meta small {
      font-size: 0.75rem;
    }
    .task-actions {
      justify-content: stretch;
    }
    .task-actions .btn {
      flex: 1 1 auto;
    }
  }</style>
